/**
 * Wire types for the INTEGRA API (§13). Hand-written rather than imported
 * from @integra/shared-types: the SPA must depend on the API's PUBLISHED
 * contract, not on server internals. If the two drift, the contract tests
 * (§13.5) are what should catch it — not a shared type silently reshaping the
 * client.
 */

export interface Page<T> {
  data: T[];
  cursor: { next: string | null; previous: string | null };
  total: number;
}

export interface Workspace {
  id: string;
  organisationId: string;
  name: string;
  slug: string;
  createdAt: string;
}

/**
 * Per-organisation white-label branding (§20.5). Mirrors the server shape; every
 * field is a nullable override that falls back to the estate brand. Colours are
 * hex DATA, painted via an injected CSS variable / SVG fill, never inline styles.
 */
export interface OrganisationBranding {
  productName: string | null;
  tagline: string | null;
  logoUrl: string | null;
  primaryColour: string | null;
  accentColour: string | null;
}

export interface Organisation {
  id: string;
  name: string;
  planTier: string;
  branding: OrganisationBranding;
}

export interface Asset {
  id: string;
  workspaceId: string;
  type: string;
  version: number;
  state: 'draft' | 'active' | 'delisted' | 'archived';
  data: Record<string, unknown>;
  lineage: { sourceId: string | null; externalId?: string | null };
  visibility: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface AssetVersion {
  version: number;
  state: string;
  data: Record<string, unknown>;
  createdAt: string;
}

/** How much review a connector's credential request needs (mirrors shared-types). */
export type ConnectorReviewLevel = 'none' | 'quick' | 'approval';

export interface ConnectorSetupField {
  key: string;
  label: string;
  required: boolean;
  help?: string;
}

/** In-product guidance for obtaining a connector's credentials (§7 onboarding). */
export interface ConnectorSetupGuide {
  developerPortalUrl: string;
  summary: string;
  steps: string[];
  scopes?: string[];
  review: ConnectorReviewLevel;
  reviewNote?: string;
  fields: ConnectorSetupField[];
  docsUrl?: string;
}

/** One item in the workspace publish feed — a published post with content + channel. */
export interface WorkspacePublication {
  id: string;
  state: string;
  lastPublishedAt: string | null;
  externalUrl: string | null;
  channel: { destinationId: string; label: string; connectorKey: string };
  asset: { id: string; type: string; data: Record<string, unknown> };
}

export interface ConnectorCatalogueEntry {
  connectorKey: string;
  name: string;
  direction: string;
  capabilities: unknown;
  setupGuide?: ConnectorSetupGuide;
  /** Present when one-click "Connect with <Platform>" is live for this connector. */
  oauth?: { supported: true; provider: string };
}

export interface ConnectorConfig {
  id: string;
  connectorKey: string;
  displayName: string;
  settings: Record<string, unknown>;
  hasCredentials: boolean;
  lastRefreshAt: string | null;
}

/** One destination a "Lane B" connection can publish to — a Page, board, account. */
export interface PublishTarget {
  id: string;
  label: string;
  subtitle?: string;
  kind?: string;
  /** The connector settings merged when this target is chosen. */
  applySettings: Record<string, string>;
}

export interface ConnectorHealth {
  connectorConfigId: string;
  connectorKey: string;
  displayName: string;
  healthy: boolean;
  detail: string | null;
  circuitState: string;
  quota: { remaining: number; limit: number } | null;
}

/** One Canva design offered in the import picker (creative source, §7.2). */
export interface CanvaDesignSummary {
  designId: string;
  title: string;
  thumbnailUrl?: string;
  pageCount?: number;
  editUrl?: string;
  viewUrl?: string;
}

/** A page of Canva designs, with a cursor for "load more". */
export interface CanvaDesignPage {
  items: CanvaDesignSummary[];
  nextCursor: string | null;
}

/** The outcome of importing one Canva design as a high-resolution Asset. */
export interface CanvaImportResult {
  assetId: string;
  title: string;
  mediaPath?: string;
  contentType: string;
  width?: number;
  height?: number;
  byteSize: number;
}

/** The outcome of generating one image with AI (creative source, Phase 2). */
export interface AiGenerationResult {
  assetId: string;
  provider: string;
  title: string;
  mediaPath?: string;
  contentType: string;
  width?: number;
  height?: number;
  byteSize: number;
}

/** Read-only connection check (§7.4). Nothing is written to the client's channel. */
export interface ConnectionProbe {
  probe: string;
  ok: boolean;
  detail: string;
  durationMs: number;
}

export interface ConnectionCheckReport {
  connectorKey: string;
  connectorName: string;
  connectorVersion: string;
  direction: string;
  reachable: boolean;
  probes: ConnectionProbe[];
  readOnly: true;
}

export interface Source {
  id: string;
  connectorConfigId: string;
  status: string;
  lastSyncAt: string | null;
}

export interface Destination {
  id: string;
  connectorConfigId: string;
  status: string;
  formatSpec: {
    acceptedDimensions: { width: number; height: number }[];
    acceptedFileTypes: string[];
    maxFileSizeBytes: number;
  };
}

export interface SyncSummary {
  syncOperationId: string;
  created: number;
  updated: number;
  skipped: number;
  errors: string[];
  assetIds: string[];
}

export interface Publication {
  id: string;
  assetId: string;
  destinationId: string;
  externalId: string | null;
  externalUrl: string | null;
  state: string;
  lastError: string | null;
  lastPublishedAt: string | null;
}

export interface DagNode {
  nodeId: string;
  action: string;
  inputs: Record<string, unknown>;
  dependsOn: string[];
  failureMode?: string;
}

export interface Campaign {
  id: string;
  name: string;
  state: string;
  dag: { nodes: DagNode[] };
  schedule: { targetStartTime: string; state: string } | null;
  createdAt: string;
}

export interface CampaignExecution {
  id: string;
  campaignId: string;
  state: string;
  attempt: number;
  startedAt: string;
  finishedAt: string | null;
}

export interface NodeExecution {
  nodeKey: string;
  state: string;
  attempt: number;
  output: Record<string, unknown> | null;
  error: string | null;
}

export interface ExecutionSummary {
  execution: CampaignExecution;
  outcome: string;
  nodeStates: Record<string, string>;
  pendingApprovals: string[];
  failures: { nodeId: string; error: string }[];
  rolledBack: string[];
}

/**
 * Measured and estimated figures for one distribution segment (organic or paid),
 * kept apart exactly as at the top level (§2.7).
 */
export interface MetricSegmentFigures {
  measured: number | null;
  estimated: number | null;
  measuredSources: string[];
}

/**
 * A dashboard metric. `measured` and `estimated` are SEPARATE fields — the API
 * never merges them, and neither may the UI (§2.7). `organic` and `paid` break
 * the same metric down by distribution so earned and bought performance sit side
 * by side in one unified view, never summed into a single blended number.
 */
export interface MetricRollup {
  metric: string;
  measured: number | null;
  estimated: number | null;
  measuredSources: string[];
  organic: MetricSegmentFigures;
  paid: MetricSegmentFigures;
  note: string | null;
}

/**
 * One day's point of a metric's trend (§2.7). `measured` and `estimated` stay
 * separate — a trend line plots the two apart, never a summed blend. `bucket` is
 * an ISO calendar day (YYYY-MM-DD).
 */
export interface MetricSeriesPoint {
  bucket: string;
  metric: string;
  measured: number | null;
  estimated: number | null;
}

export interface DashboardResponse {
  scope: string;
  window: { from: string; to: string };
  metrics: MetricRollup[];
  /** Daily trend points across the window, one per (day, metric). */
  series: MetricSeriesPoint[];
}

/**
 * A source-level metric — a data source's OWN figure (e.g. a GA4 property's
 * sessions), not a channel post's. `measured` and `estimated` stay separate
 * (§2.7); `source`/`externalId` say whose data this is. Never blended with the
 * per-channel dashboard metrics.
 */
export interface SourceMetricRollup {
  source: string;
  externalId: string | null;
  metric: string;
  measured: number | null;
  estimated: number | null;
  note: string | null;
}

export interface SiteAnalyticsResponse {
  window: { from: string; to: string };
  metrics: SourceMetricRollup[];
}

export type PlanTierName = 'bronze' | 'silver' | 'gold';

export interface PlanLimits {
  channels: number | null;
  campaignsPerMonth: number | null;
  assets: number | null;
  rendersPerMonth: number | null;
  apiCallsPerMonth: number | null;
}

export interface TierPricing {
  baseMonthly: number;
  included: { publishes: number; renders: number };
  overage: { publish: number; render: number };
}

export interface BillLine {
  metric: 'publish' | 'render';
  used: number;
  included: number;
  billable: number;
  unitPrice: number;
  amount: number;
}

export interface BillEstimate {
  tier: PlanTierName;
  base: number;
  overage: BillLine[];
  overageTotal: number;
  total: number;
  currency: string;
}

export interface PlanCatalogueEntry {
  tier: PlanTierName;
  limits: PlanLimits;
  pricing: TierPricing;
}

export interface PlanSummary {
  tier: PlanTierName;
  limits: PlanLimits;
  usage: { channels: number; assets: number; publishes: number; renders: number };
  bill: BillEstimate;
  catalog: PlanCatalogueEntry[];
  currency: string;
}

export interface UsageResponse {
  window: { from: string; to: string };
  usage: { metric: string; quantity: number; unit: string }[];
  plan: PlanSummary;
}

// --- AI Ingress: Import a Content Manifest as drafts (ai-ingress §4A) ---

/**
 * A Content Manifest (v1). A post is an open bag of content fields; `type` and
 * `visibility` are lifted to the asset, everything else is carried into `data`
 * verbatim. Kept loose on purpose — the server is the authority on the shape.
 */
export interface ContentManifest {
  manifestVersion: '1';
  workspaceHint?: string;
  posts: Record<string, unknown>[];
}

/** One post's outcome from an ingest batch. */
export interface IngestResult {
  ref?: string;
  assetId?: string;
  error?: string;
}

export interface IngestBatchResult {
  batchId: string;
  results: IngestResult[];
}

// --- Weekly review (§20.5) ---

/** One channel signal, measured and estimated kept apart — never summed (§2.7). */
export interface WeeklyReviewSignalFigure {
  metric: string;
  measured: number | null;
  estimated: number | null;
  measuredSources: string[];
}

/** A ranked performer. `measured` is the ranking basis; `estimated` never is. */
export interface WeeklyReviewRankedEntry {
  id: string;
  label: string;
  measured: number;
  estimated: number | null;
  measuredSources: string[];
}

export interface WeeklyReviewRanking {
  dimension: 'campaign' | 'asset' | 'assetType';
  rankedBy: string;
  entries: WeeklyReviewRankedEntry[];
}

export interface WeeklyReviewChannel {
  destinationId: string;
  connectorConfigId: string | null;
  leadingSignal: string | null;
  signals: WeeklyReviewSignalFigure[];
  rankings: WeeklyReviewRanking[];
  nonAdditiveSignalsPresent: string[];
  hasMeasuredData: boolean;
}

export interface WeeklyReviewDimensionAvailability {
  dimension: string;
  available: boolean;
  reason: string;
}

export interface WeeklyReviewResponse {
  window: { from: string; to: string };
  channels: WeeklyReviewChannel[];
  dimensions: WeeklyReviewDimensionAvailability[];
  /**
   * Source-level (site/audience) figures over the same window — a GA4 property's
   * sessions, users, page views. Carried alongside the channels, never ranked
   * among them: a website session is not a channel post's measurement (§2.7).
   */
  sources: SourceMetricRollup[];
}

export interface BrandSystem {
  colours: Record<string, string>;
  typography: Record<string, { family: string; fallbacks: string[]; weight?: number }>;
  spacing: { baseUnit: number; scale: Record<string, number> };
  platformConstraints: Record<string, unknown>;
}

export interface Template {
  id: string;
  name: string;
  templateType: string;
  layout: {
    canvas: { width: number; height: number; background: string };
    elements: unknown[];
  };
}

export interface RenderResult {
  asset: Asset;
  mediaRef: string;
  width: number;
  height: number;
  byteSize: number;
}

// --- Platform administration (§6) ---

export interface EstateOrganisation {
  id: string;
  name: string;
  planTier: string;
  workspaces: number;
  users: number;
  createdAt: string;
}

export interface EstateActivity {
  operation: string;
  actorId: string | null;
  workspaceId: string | null;
  createdAt: string;
}

export interface EstateOverview {
  totals: {
    organisations: number;
    workspaces: number;
    users: number;
    campaigns: number;
    destinations: number;
    connectors: number;
  };
  organisations: EstateOrganisation[];
  recentActivity: EstateActivity[];
}

// --- Broadcast: publish to every connected channel at once (§2.3) ---

export interface BroadcastResult {
  destinationId: string;
  connectorKey: string;
  channelName: string;
  state: 'live' | 'pending' | 'failed';
  verified: boolean;
  externalUrl: string | null;
  error: string | null;
}

export interface BroadcastOutcome {
  assetId: string;
  attempted: number;
  live: number;
  pending: number;
  failed: number;
  results: BroadcastResult[];
}

// --- Auto-repurposing: one hero adapted per channel (§9) ---

export interface ChannelAdaptation {
  field: string;
  original: string;
  adapted: string;
  reason: string;
}

export interface RepurposeChannel {
  destinationId: string;
  connectorKey: string;
  channelName: string;
  targetDimension: { width: number; height: number } | null;
  targetFileType: string | null;
  adaptations: ChannelAdaptation[];
}

export interface RepurposePreview {
  assetId: string;
  channels: RepurposeChannel[];
}

// --- Pre-broadcast compatibility & fit report (§6.4, §9) ---

export interface CompatibilityFinding {
  kind: 'adapts' | 'blocks';
  field: string;
  detail: string;
}

export interface ChannelCompatibility {
  destinationId: string;
  connectorKey: string;
  channelName: string;
  status: 'ready' | 'adapts' | 'blocked';
  findings: CompatibilityFinding[];
  citation: { sourceUrl: string; lastVerified: string; specVersion: string } | null;
}

export interface CompatibilityReport {
  assetId: string;
  ready: number;
  adapts: number;
  blocked: number;
  channels: ChannelCompatibility[];
}

// --- Brand-safety pre-flight (§21) ---

export interface BrandSafetyConcern {
  rule: string;
  field: string;
  term: string;
  severity: 'block' | 'warn';
  detail: string;
}

export interface BrandSafetyReport {
  ok: boolean;
  concerns: BrandSafetyConcern[];
}

/** A workspace's configured brand-safety rules (§21). */
export interface BrandSafetyRuleset {
  prohibitedTerms: string[];
  reviewTerms: string[];
}

/** A workspace's Adaptation Engine options (§9). */
export interface AdaptationOptions {
  brandedOverlay: boolean;
}

/** A required disclosure that must appear in published content (§21). */
export interface RequiredDisclaimer {
  id: string;
  text: string;
}

/** A workspace's compliance pack (§21): regulated-industry rules on top of brand safety. */
export interface CompliancePack {
  industry: string | null;
  requiredDisclaimers: RequiredDisclaimer[];
  prohibitedClaims: string[];
  mode: 'block' | 'append';
}

// --- Enterprise auth: SAML SSO + SCIM provisioning (§14.5) ---

export type WorkspaceRoleValue = 'owner' | 'admin' | 'editor' | 'viewer' | 'api_only';

/** A per-organisation SAML SSO connection. The IdP certificate is public. */
export interface SsoConnection {
  id: string;
  organisationId: string;
  enabled: boolean;
  idpEntityId: string;
  idpSsoUrl: string;
  idpCertificate: string;
  spEntityId: string;
  defaultWorkspaceRole: WorkspaceRoleValue;
  emailDomain: string | null;
  createdAt: string;
  updatedAt: string;
}

/** The editable SSO connection fields sent on save. */
export interface SsoConnectionInput {
  idpEntityId: string;
  idpSsoUrl: string;
  idpCertificate: string;
  spEntityId: string;
  defaultWorkspaceRole: WorkspaceRoleValue;
  emailDomain: string | null;
  enabled: boolean;
}

/** SCIM provisioning token metadata — the secret is never returned after creation. */
export interface ScimTokenSummary {
  id: string;
  organisationId: string;
  label: string;
  createdBy: string | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

/** A freshly-minted SCIM token: the plaintext `token` is shown exactly once. */
export interface ScimTokenCreated extends ScimTokenSummary {
  token: string;
}

// --- Attribution & UTM (§20.5) ---

export interface UtmParams {
  source: string | null;
  medium: string | null;
  campaign: string | null;
  content: string | null;
  term: string | null;
}

/** A tracked short link with its UTM tags, attribution keys and click count. */
export interface TrackedLink {
  id: string;
  workspaceId: string;
  slug: string;
  targetUrl: string;
  label: string;
  utm: UtmParams;
  campaignId: string | null;
  assetId: string | null;
  destinationId: string | null;
  createdAt: string;
  updatedAt: string;
  clicks: number;
  /** The public short URL, `<api>/l/<slug>`. */
  shortUrl: string;
}

export interface ReferrerStat {
  host: string;
  clicks: number;
}

export interface CreateTrackedLinkInput {
  targetUrl: string;
  label: string;
  utm: UtmParams;
  campaignId: string | null;
  assetId: string | null;
  destinationId: string | null;
}

// --- Client reporting + portals (§20.5) ---

export interface ReportMetric {
  metric: string;
  measured: number | null;
  estimated: number | null;
  measuredSources: string[];
}

/** The frozen snapshot a report captures — shown identically in-app and on the portal. */
export interface ReportSnapshot {
  workspaceId: string;
  organisationName: string;
  branding: Record<string, unknown>;
  period: { from: string; to: string };
  generatedAt: string;
  metrics: ReportMetric[];
  topLinks: { label: string; clicks: number }[];
}

export interface Report {
  id: string;
  workspaceId: string;
  title: string;
  periodFrom: string;
  periodTo: string;
  snapshot: ReportSnapshot;
  shared: boolean;
  createdAt: string;
  updatedAt: string;
}

// --- Localization (§20.5) ---

export interface AssetTranslation {
  id: string;
  assetId: string;
  locale: string;
  fields: Record<string, string>;
  updatedAt: string;
}

// --- Public API + webhooks (§20.5) ---

export const WEBHOOK_EVENTS = [
  'asset.published',
  'campaign.completed',
  'engagement.reply_approved',
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

/** A user's saved filter/view for a data surface (§17.2). */
export interface SavedView {
  id: string;
  surface: string;
  name: string;
  config: Record<string, unknown>;
  updatedAt: string;
}

export interface WebhookSubscription {
  id: string;
  url: string;
  events: string[];
  active: boolean;
  description: string | null;
  lastDeliveryAt: string | null;
  createdAt: string;
}

/** A freshly-created subscription: the signing `secret` is shown exactly once. */
export interface WebhookSubscriptionCreated extends WebhookSubscription {
  secret: string;
}

export interface WebhookDelivery {
  id: string;
  event: string;
  status: string;
  responseCode: number | null;
  error: string | null;
  createdAt: string;
}

// --- AI campaign assistant (§20.5) ---

export interface ChannelCopy {
  channel: string;
  headline: string;
  body: string;
}

export interface CampaignDraft {
  name: string;
  heroTitle: string;
  heroBody: string;
  channels: ChannelCopy[];
  disclosure: string;
}

// --- AI copywriting: audience-tailored copy per channel (§9, §21) ---

export interface CopyVariant {
  destinationId: string;
  connectorKey: string;
  channelName: string;
  headline: string;
  body: string;
  callToAction: string;
  rationale: string;
  characterLimit: number | null;
  bodyLength: number;
  withinLimit: boolean;
  aiGenerated: true;
}

export interface CopyGenerationResult {
  assetId: string;
  audience: string | null;
  disclosure: string;
  model: string;
  variants: CopyVariant[];
}

// --- A/B experimentation with auto-promote (§20.5) ---

export type ExperimentStatus = 'draft' | 'running' | 'decided' | 'stopped';

export interface ExperimentVariant {
  id: string;
  assetId: string;
  label: string;
  publicationId: string | null;
  externalId: string | null;
}

export interface Experiment {
  id: string;
  workspaceId: string;
  destinationId: string;
  metric: string;
  status: ExperimentStatus;
  variants: ExperimentVariant[];
  winnerVariantId: string | null;
  rationale: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VariantStanding {
  variantId: string;
  label: string;
  assetId: string;
  measured: number | null;
}

export interface ExperimentDecision {
  decided: boolean;
  winnerVariantId: string | null;
  rationale: string;
}

export interface ExperimentEvaluation {
  experiment: Experiment;
  standings: VariantStanding[];
  decision: ExperimentDecision;
}

// --- HIVE optimisation intelligence (§10.3) ---

export type OptimisationKind = 'timing' | 'channel' | 'creative' | 'budget' | 'other';

export interface OptimisationRecommendation {
  kind: OptimisationKind;
  target: string;
  action: string;
  confidence: number;
  rationale: string;
  source: 'hive';
}

export interface OptimisationResult {
  workspaceId: string;
  disclosure: string;
  recommendations: OptimisationRecommendation[];
}

// --- Channel Spec Library: the gold standard per channel (§6.4, §9) ---

export type ChannelType = 'social' | 'commerce' | 'messaging' | 'ingest' | 'cms';

export interface ChannelFormatSpec {
  acceptedDimensions: { width: number; height: number }[];
  acceptedFileTypes: string[];
  maxFileSizeBytes: number;
  characterLimits: Record<string, number>;
  acceptedVideoTypes?: string[];
  maxDurationSeconds?: number;
  minDurationSeconds?: number;
  maxVideoBytes?: number;
  acceptedAspectRatios?: { width: number; height: number }[];
}

export interface ChannelBestPractices {
  summary: string;
  recommendedAspectRatios?: string[];
  recommendedDurationSeconds?: { min?: number; max?: number; sweetSpot?: number };
  hashtags?: string;
  tone?: string;
  notes?: string[];
}

export interface SpecProvenance {
  sourceUrl: string;
  lastVerified: string;
  specVersion: string;
}

export interface ChannelSpec {
  connectorKey: string;
  displayName: string;
  channelType: ChannelType;
  format: ChannelFormatSpec;
  bestPractices: ChannelBestPractices;
  provenance: SpecProvenance;
}

// --- Spec drift health (§9 Phase 5) ---

export type SpecHealthStatus = 'ok' | 'stale' | 'drift';
export type ConsistencyStatus = 'aligned' | 'diverged' | 'unknown';

export interface SpecHealth {
  connectorKey: string;
  displayName: string;
  channelType: ChannelType;
  status: SpecHealthStatus;
  freshness: { lastVerified: string; ageDays: number; slaDays: number; stale: boolean };
  consistency: { status: ConsistencyStatus; differences: string[] };
  bestPracticesVerification: 'manual-review-only';
  provenance: SpecProvenance;
}

export interface SpecHealthReport {
  generatedAt: string;
  slaDays: number;
  total: number;
  ok: number;
  stale: number;
  drift: number;
  channels: SpecHealth[];
}

// --- Engagement (§20.6): the inbox + governed replies ---

export type EngagementKind = 'comment' | 'mention' | 'direct_message' | 'review';
export type EngagementItemStatus = 'open' | 'replied' | 'dismissed';
export type EngagementReplyStatus = 'pending' | 'approved' | 'rejected' | 'failed';

export interface EngagementItem {
  id: string;
  connectorConfigId: string;
  source: string;
  externalId: string;
  kind: EngagementKind;
  authorName: string;
  authorHandle: string | null;
  body: string;
  permalink: string | null;
  parentExternalId: string | null;
  status: EngagementItemStatus;
  receivedAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface EngagementReply {
  id: string;
  engagementItemId: string;
  connectorConfigId: string;
  status: EngagementReplyStatus;
  draftText: string;
  reason: string | null;
  requestedBy: string;
  decidedBy: string | null;
  correlationId: string;
  decidedAt: string | null;
  createdAt: string;
}
